import { OneMedia } from '@prismamedia/one-components';
import {
  BlockFragment,
  GetCampaignsQuery_campaigns_Campaign_sections_Section_blocks_Block,
  SectionFragment,
} from '__generated__/queries-newsletter';
import { EditorState } from 'draft-js';
import { BlockItemType } from 'pages/CampaignPage/CampaignEditPage/ContentPage/Items/utils';
import {
  append,
  concat,
  cond,
  findIndex,
  lensPath,
  lensProp,
  map,
  over,
  propEq,
  remove,
  set,
  T,
} from 'ramda';
import { DropResult } from 'react-beautiful-dnd';
import { EnrichedCampaignSection, NewsletterSectionConfig } from './Sections';
import {
  addOrRemoveSections,
  buildSectionsDraftFromNewsletterConfig,
  compareSectionAmount,
} from './Sections/utils';
import { emptyBlock, emptySection } from './utils/emptyTypes';
import { manageDrag } from './utils/manageDrag';
import { validateField } from './utils/validateField';

enum CampaignSectionActions {
  ADD_BLOCK = 'ADD_BLOCK',
  ADD_SECTION = 'ADD_SECTION',
  DELETE_BLOCK = 'DELETE_BLOCK',
  DELETE_SECTION = 'DELETE_SECTION',
  DRAG_END = 'DRAG_END',
  SET_FIELD_VALUE = 'SET_FIELD_VALUE',
  SET_LEADIN = 'SET_LEADIN',
  REGULATE_SECTIONS_FROM_TEMPLATE = 'REGULATE_SECTIONS_FROM_TEMPLATE',
}

export interface CampaignSectionAction {
  type: CampaignSectionActions;
  data?: any;
}

export const actions = {
  dragEnd: ({
    result,
    articles,
  }: {
    result: DropResult;
    articles: Record<string, any>;
  }) => ({
    type: CampaignSectionActions.DRAG_END,
    data: {
      result,
      articles,
    },
  }),
  addSection: () => ({
    type: CampaignSectionActions.ADD_SECTION,
  }),
  deleteSection: ({ sectionId }: { sectionId: string }) => ({
    type: CampaignSectionActions.DELETE_SECTION,
    data: {
      sectionId,
    },
  }),
  deleteBlock: ({
    sectionId,
    blockId,
  }: {
    sectionId: string;
    blockId: string;
  }) => ({
    type: CampaignSectionActions.DELETE_BLOCK,
    data: {
      sectionId,
      blockId,
    },
  }),
  addBlock: ({
    sectionId,
    blockType,
    articleData,
  }: {
    sectionId: string;
    blockType: string;
    articleData?: BlockItemType;
  }) => ({
    type: CampaignSectionActions.ADD_BLOCK,
    data: {
      sectionId,
      blockType,
      articleData,
    },
  }),
  setLeadIn: ({
    sectionId,
    blockId,
  }: {
    sectionId: string;
    blockId: string;
  }) => ({
    type: CampaignSectionActions.SET_LEADIN,
    data: {
      sectionId,
      blockId,
    },
  }),
  updateField: ({
    sectionId,
    blockId = null,
    field,
    value,
  }: {
    sectionId: string;
    blockId?: string | null;
    field: string;
    value: string | OneMedia | EditorState;
  }) => ({
    type: CampaignSectionActions.SET_FIELD_VALUE,
    data: {
      sectionId,
      blockId,
      field,
      value,
    },
  }),
  regulateSectionsFromTemplate: ({
    newsletterSectionConfig,
  }: {
    newsletterSectionConfig: any;
  }) => ({
    type: CampaignSectionActions.REGULATE_SECTIONS_FROM_TEMPLATE,
    data: {
      newsletterSectionConfig,
    },
  }),
};

export const reducer = (
  state: EnrichedCampaignSection[],
  action: CampaignSectionAction,
): SectionFragment[] => {
  switch (action.type) {
    case CampaignSectionActions.DRAG_END: {
      return manageDrag(action.data.result, action.data.articles, state);
    }
    case CampaignSectionActions.ADD_SECTION: {
      return [...state, emptySection(state.length)];
    }
    case CampaignSectionActions.DELETE_SECTION: {
      const index = findIndex(propEq('id', action.data.sectionId))(state);
      return remove<SectionFragment>(index, 1, state);
    }
    case CampaignSectionActions.DELETE_BLOCK: {
      const sectionIndex = findIndex(propEq('id', action.data.sectionId))(
        state,
      );
      const blockIndex = findIndex(propEq('id', action.data.blockId))(
        state[sectionIndex].blocks,
      );
      const lens = lensPath([sectionIndex, 'blocks']);
      return over(
        lens,
        (
          blocks: GetCampaignsQuery_campaigns_Campaign_sections_Section_blocks_Block[],
        ) => remove(blockIndex, 1, blocks),
        state,
      );
    }
    case CampaignSectionActions.ADD_BLOCK: {
      const { articleData, blockType, sectionId } = action.data;
      const sectionIndex = findIndex(propEq('id', sectionId))(state);
      const lens = lensPath([sectionIndex, 'blocks']);

      const {
        validation: isUrlValid,
        validationMessage: urlInMessage,
      } = validateField('url', articleData?.url);

      const newBlock = {
        title: articleData?.title,
        url: articleData?.url,
        urlInError: !isUrlValid,
        urlInMessage,
        surTitle: articleData?.surTitle,
        content: articleData?.content,
        media: articleData ? articleData.media : null,
        blockType,
        isAutoPromo: blockType === 'autoPromo',
      };

      return over(
        lens,
        (blocks: BlockFragment[]) =>
          append(
            emptyBlock({
              ...newBlock,
              position: blocks?.length,
            }),
            blocks,
          ),
        state,
      );
    }
    case CampaignSectionActions.SET_LEADIN: {
      const sectionIndex = findIndex(propEq('id', action.data.sectionId))(
        state,
      );
      const sectionBlocks = lensPath([sectionIndex, 'blocks']);
      const lensLeadIn = (lensProp as any)('leadIn');
      const testBlock = cond([
        [
          propEq('id', action.data.blockId),
          (block: BlockFragment) => set(lensLeadIn, !block.leadIn, block),
        ],
        [T, (block: BlockFragment) => set(lensLeadIn, false, block)],
      ]);
      return over(sectionBlocks, map(testBlock as any), state);
    }
    case CampaignSectionActions.SET_FIELD_VALUE: {
      const sectionIndex = findIndex(propEq('id', action.data.sectionId))(
        state,
      );
      let path: (string | number)[] = [sectionIndex];
      if (action.data.blockId) {
        const blockIndex = findIndex(propEq('id', action.data.blockId))(
          state[sectionIndex].blocks,
        );
        path = concat(path, ['blocks', blockIndex]);
      }
      // eslint-disable-next-line fp/no-mutating-methods
      path.push(action.data.field);
      const lensToField = lensPath(path);
      return set(lensToField, action.data.value, state);
    }
    case CampaignSectionActions.REGULATE_SECTIONS_FROM_TEMPLATE: {
      const { newsletterSectionConfig } = action.data;

      if (!newsletterSectionConfig) {
        return state;
      }

      const parsedNewsletterSectionConfig: NewsletterSectionConfig[] = JSON.parse(
        newsletterSectionConfig,
      );

      let sectionsDraft: EnrichedCampaignSection[] =
        state.length > 0
          ? state.map(({ ...rest }, i) => ({
              maxBlocksAmount:
                parsedNewsletterSectionConfig[i]?.sectionResourceCount,
              ...rest,
            }))
          : buildSectionsDraftFromNewsletterConfig(
              parsedNewsletterSectionConfig,
            );

      const sectionAmountDifference = compareSectionAmount(
        sectionsDraft,
        parsedNewsletterSectionConfig,
      );

      if (sectionAmountDifference !== 0) {
        sectionsDraft = addOrRemoveSections(
          sectionsDraft,
          sectionAmountDifference,
        );
      }

      return sectionsDraft;
    }
    default: {
      return state;
    }
  }
};
